// src/utils/confirmModal.js
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";

/**
 * 전역 확인 모달을 띄우고 사용자가 "확인/취소"를 누를 때까지 기다립니다.
 * @param {Object} opts
 * @param {string} opts.title 모달 제목
 * @param {string} opts.message 모달 내용
 * @param {string} [opts.confirmText="확인"]
 * @param {string} [opts.cancelText="취소"]
 * @param {"info"|"error"|"warning"|"success"} [opts.variant="info"]
 * @returns {Promise<boolean>} 확인 = true / 취소 = false
 */
export function showConfirm({
  title,
  message,
  confirmText = "확인",
  cancelText = "취소",
  variant = "info",
}) {
  // 모달 컨테이너 생성
  const containerId = "global-confirm-modal-root";
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
  }

  // 루트 생성
  const root = createRoot(container);

  // 색상 변형
  const variantColor =
    {
      info: "from-blue-600 to-purple-600",
      error: "from-red-600 to-rose-600",
      warning: "from-amber-500 to-orange-600",
      success: "from-emerald-600 to-teal-600",
    }[variant] || "from-blue-600 to-purple-600";

  // 모달 컴포넌트
  function ConfirmModal({ onResolve }) {
    useEffect(() => {
      const onKey = (e) => {
        if (e.key === "Escape") onResolve(false);
        if (e.key === "Enter") onResolve(true);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [onResolve]);

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        aria-modal="true"
        role="dialog"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => onResolve(false)}
        />
        {/* Dialog */}
        <div className="relative mx-4 w-full max-w-md rounded-2xl border border-gray-800 bg-gray-900 text-white shadow-2xl">
          <div
            className={`rounded-t-2xl bg-gradient-to-r ${variantColor} h-1`}
          />
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-gray-300 leading-relaxed">{message}</p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => onResolve(false)}
                className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={() => onResolve(true)}
                className={`px-4 py-2 rounded-lg text-white bg-gradient-to-r ${variantColor} hover:opacity-95 transition-opacity`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Promise로 결과 반환
  return new Promise((resolve) => {
    const handleResolve = (val) => {
      try {
        root.unmount();
        // 컨테이너 정리(선택)
        container.parentNode && container.parentNode.removeChild(container);
      } finally {
        resolve(val);
      }
    };
    root.render(<ConfirmModal onResolve={handleResolve} />);
  });
}
